import React from 'react';
import { Stack, Box } from '@mui/material';
import { colors, Button, Typography } from '@sweep-io/sweep-design';
import { Info, Check } from '@sweep-io/sweep-design/dist/icons';
import { CenteredCircularProgress } from '../CenteredCircularProgress';
import { useBuildDefinitions } from './useBuildDefinitions';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectActiveChatId,
  selectActiveAgentId,
  selectBuildDefinitionStatus,
  selectBuildDefinitionText,
  selectLatestBuildDefinitionId,
  setBuildDefinitionStatus,
} from '../../aiAgentCenter/agentsChatsReducer';
import { useAgentChatInstance } from '../../aiAgentCenter/AgentChatInstanceContext';
import { ResizableInlinePanel } from '../panel/ResizableInlinePanel';
import { useArtifactPanelWidth } from '../../../hooks/useArtifactPanelWidth';
import { MarkdownWrapper } from '../MarkdownWrapper';
import { BuildDefinitionStatus } from '@server/ai';
import useAgentsChats from '../../aiAgentCenter/useAgentsChats';
import { AgentInputMode } from '../../aiAgentCenter/agent-input/agentInputModeTypes';

export const BuildDefinitionPanel: React.FC = () => {
  const {
    isBuildDefinitionPanelOpen,
    closeBuildDefinitionPanel,
    currentBuildDefinitionId,
    isStreaming,
  } = useBuildDefinitions();

  const dispatch = useDispatch();
  const { chatInstanceId } = useAgentChatInstance();
  const activeAgentId = useSelector(selectActiveAgentId);
  const activeChatId = useSelector(selectActiveChatId(chatInstanceId));
  const status = useSelector(
    selectBuildDefinitionStatus(activeChatId ?? '', currentBuildDefinitionId ?? ''),
  );
  const buildDefinitionText = useSelector(
    selectBuildDefinitionText(activeChatId ?? '', currentBuildDefinitionId ?? ''),
  );
  const latestBuildDefinitionId = useSelector(selectLatestBuildDefinitionId(activeChatId ?? ''));
  const { onConfirmMessage, activeAgent } = useAgentsChats();

  const { panelWidth, updatePanelWidth, minWidth, maxWidth } = useArtifactPanelWidth();

  if (!isBuildDefinitionPanelOpen || !currentBuildDefinitionId) {
    return null;
  }

  const resolvedStatus = status ?? BuildDefinitionStatus.Draft;
  const isBuildDisabled = isStreaming || currentBuildDefinitionId !== latestBuildDefinitionId;

  const handleBuildClick = () => {
    if (
      isBuildDisabled ||
      resolvedStatus !== BuildDefinitionStatus.Draft ||
      !activeAgent ||
      !activeAgentId ||
      !activeChatId
    )
      return;

    dispatch(
      setBuildDefinitionStatus({
        agentId: activeAgentId,
        chatId: activeChatId,
        buildDefinitionId: currentBuildDefinitionId,
        status: BuildDefinitionStatus.Building,
      }),
    );
    onConfirmMessage({
      messageStr: 'build',
      agentId: activeAgent.id,
      agentType: activeAgent.type,
      advancedMode: AgentInputMode.BuildMode,
    });
  };

  // CTA rendering based on status
  const renderCTA = () => {
    switch (resolvedStatus) {
      case BuildDefinitionStatus.Draft:
        return (
          <Button
            variant="primary"
            size="large"
            onClick={handleBuildClick}
            disabled={isBuildDisabled}
          >
            Deploy
          </Button>
        );
      case BuildDefinitionStatus.Building:
        return (
          <Stack direction="row" alignItems="center" gap={1}>
            <CenteredCircularProgress size={20} color={colors.grey[700]} minHeight="auto" />
            <Typography variant="body" color={colors.grey[700]}>
              Deploying
            </Typography>
          </Stack>
        );
      case BuildDefinitionStatus.Success:
        return (
          <Stack direction="row" alignItems="center" gap={1}>
            <Check size="medium" color={colors.feedback.success} />
            <Typography variant="body" color={colors.feedback.success}>
              Deployed successfully
            </Typography>
          </Stack>
        );
      case BuildDefinitionStatus.Failed:
        return (
          <Typography variant="body" color={colors.feedback.destructive} textAlign="center">
            Deploy failed
          </Typography>
        );
    }
  };

  const header = (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      width="100%"
      padding="12px 24px"
    >
      <Typography variant="h4-medium">Metadata Definition</Typography>
    </Stack>
  );

  return (
    <ResizableInlinePanel
      panelWidth={panelWidth}
      onResize={updatePanelWidth}
      minWidth={minWidth}
      maxWidth={maxWidth}
      onClose={closeBuildDefinitionPanel}
      header={header}
      showHeaderBorder={true}
    >
      <Stack
        sx={{
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            padding: 3,
          }}
        >
          {!buildDefinitionText ? (
            <CenteredCircularProgress />
          ) : (
            <MarkdownWrapper
              content={buildDefinitionText}
              removeMargins={false}
              shouldShowTableCopyButtons={true}
            />
          )}
        </Box>
        <Box
          sx={{
            padding: '16px 24px',
            height: '80px',
            borderTop: `1px solid ${colors.grey[200]}`,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent:
              resolvedStatus === BuildDefinitionStatus.Draft && !isBuildDisabled
                ? 'space-between'
                : 'flex-end',
          }}
        >
          {resolvedStatus === BuildDefinitionStatus.Draft && !isBuildDisabled && (
            <Stack direction="row" alignItems="center" gap={1}>
              <Info size="small" color={colors.grey[700]} />
              <Typography variant="caption" color={colors.grey[700]}>
                The new metadata will be built and deployed
              </Typography>
            </Stack>
          )}
          {renderCTA()}
        </Box>
      </Stack>
    </ResizableInlinePanel>
  );
};
